import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

// mui icons
import Assignment from '@mui/icons-material/Assignment';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';
import CardIcon from '../md/components/Card/CardIcon.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';

import { setFirmDropdowns } from '../store/slices/firmInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import KReorderableList from '../components/KReorderableList';
import KSelect from '../components/KSelect';

import { cardTitle } from '../assets/jss/material-dashboard-pro-react.jsx';

const dropdownEditorStyle = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: '15px',
    marginBottom: '0px',
  },
  dropdownSelectDiv: {
    marginBottom: '16px',
  },
  reorderingDiv: {
    marginTop: '32px',
    marginLeft: '32px',
  },
};

class DropdownEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dropdownName: '',
      items: [],
      quasiRedux: [],
    };
  }

  componentDidMount() {
    this.getStateFromRedux();
  }

  getStateFromRedux() {
    this.setState({ quasiRedux: this.props.dropdowns });
  }

  filterForEditabilityAndEditingAdvisability(reduxState) {
    const editableDropdowns = reduxState.filter((dd) => dd.editable);
    return editableDropdowns.filter((dd) => dd.name !== 'Activity Type');
  }

  handleDropdownNameChange(event) {
    const newName = event.target.value;
    const oldName = this.state.dropdownName;
    if (oldName) {
      this.saveCurrentStateToQuasiRedux();
    }
    this.setStateForDropdownName(newName);
  }

  setStateForDropdownName(dropdownName) {
    const ddStruct = this.state.quasiRedux.find((stct) => stct.name === dropdownName);
    const items = ddStruct.items;
    this.setState({ dropdownName, items });
  }

  saveCurrentStateToQuasiRedux() {
    const currentDdName = this.state.dropdownName;
    if (!currentDdName) {
      return;
    }
    const dropdownToModify = this.state.quasiRedux.find((dd) => dd.name === currentDdName);
    const otherDds = this.state.quasiRedux.filter((dd) => dd.name !== currentDdName);
    const modifiedDd = { ...dropdownToModify, items: this.state.items };
    const newList = [...otherDds, modifiedDd];
    const sorted = newList.sort((dd1, dd2) => dd1.dropdownId - dd2.dropdownId);
    this.setState({ quasiRedux: sorted });
  }

  saveNewListToQuasiRedux(dropdownName, reorderedList) {
    if (!dropdownName) {
      return;
    }
    const newItems = this.constructItemsFromOrderedList(reorderedList);
    this.setState({ items: newItems });
    const dropdownToModify = this.state.quasiRedux.find((dd) => dd.name === dropdownName);
    const otherDds = this.state.quasiRedux.filter((dd) => dd.name !== dropdownName);
    const modifiedDd = { ...dropdownToModify, items: newItems };
    const newList = [...otherDds, modifiedDd];
    const sorted = newList.sort((dd1, dd2) => dd1.dropdownId - dd2.dropdownId);
    this.setState({ quasiRedux: sorted }, this.saveToDb);
  }

  constructItemsFromOrderedList(listFromReordering) {
    const prevItems = this.state.items;
    const newItems = listFromReordering.map((reorderedItem, index) =>
      this.findOrConstructDropdownItem(reorderedItem, index, prevItems)
    );
    return newItems;
  }

  findOrConstructDropdownItem(reorderedItem, index, dropdownItems) {
    // reorderedItem is like {id: x, text: y, deletable: T/F}
    // return value must by like {dropdownItemId: x, position: w, text: y, isProtected: T/F, enabled: T/F}

    if (reorderedItem.id) {
      const matchingDdItem = dropdownItems.find((i) => i.dropdownItemId === reorderedItem.id);
      const positionedItem = { ...matchingDdItem, position: index };
      return positionedItem;
    } else {
      const matchingDdItem = dropdownItems.find((i) => i.text === reorderedItem.text);
      if (matchingDdItem) {
        // should be very unlikely
        const positionedItem = { ...matchingDdItem, position: index };
        return positionedItem;
      } else {
        return {
          dropdownItemId: 0,
          enabled: true,
          isProtected: false,
          position: index,
          text: reorderedItem.text,
        };
      }
    }
  }

  saveNewOrdering(id, newList) {
    this.saveNewListToQuasiRedux(id, newList); // will call saveToDb() after setting state
  }

  saveToDb() {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/customizableDropdowns`;
    const apiStruct = { dropdowns: this.state.quasiRedux };
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      const dropdowns = json.dropdowns;
      this.props.setFirmDropdowns(dropdowns);
      this.getStateFromRedux();
      ReduxLoader.initProspectLeadTemperatureInclusionFilterState();
      this.setState({ dropdownName: '' });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  filterAndSortItems(items) {
    const filteredItems = items.filter((item) => item.enabled);
    const sortedItems = filteredItems.sort((i1, i2) => i1.position - i2.position);
    return sortedItems;
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;
    const filteredState = this.filterForEditabilityAndEditingAdvisability(this.state.quasiRedux);
    const dropdownNames = filteredState.map((stct) => stct.name).sort();
    const dropdownItems = this.filterAndSortItems(this.state.items);
    const itemsConfiguredForList = dropdownItems.map((item) => {
      return { id: item.dropdownItemId, text: item.text, deletable: !item.isProtected };
    });

    return (
      <div>
        <Grid container>
          <Grid item md={3} />
          <Grid item md={6}>
            <Card>
              <CardHeader icon>
                <CardIcon color="allevaOrange">
                  <Assignment />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>Customize Dropdowns</h4>
              </CardHeader>
              <CardBody>
                <div className={classes.dropdownSelectDiv}>
                  <KSelect
                    id="dropdownSelect"
                    name="dropdown"
                    fullWidth={true}
                    label="Choose a Dropdown to Customize"
                    displayEmpty={true}
                    disabledOption="Choose..."
                    liveOptions={dropdownNames}
                    value={this.state.dropdownName}
                    onChange={(event) => this.handleDropdownNameChange(event)}
                  />
                </div>
                {this.state.dropdownName && (
                  <div className={classes.reorderingDiv}>
                    <KReorderableList
                      id={this.state.dropdownName}
                      addable={true}
                      items={itemsConfiguredForList}
                      onSave={(id, newList) => {
                        this.saveNewOrdering(id, newList);
                      }}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Grid>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  dropdowns: state.firmInfo.dropdowns,
});

const actionProducers = {
  setFirmDropdowns,
};

export default connect(mapStateToProps, actionProducers)(withStyles(dropdownEditorStyle)(DropdownEditor));
